import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** Shared layout for loading / error / empty / closed states. */
export function StatePanel({
  icon: Icon,
  title,
  text,
  tone = 'neutral',
  busy = false,
  actions,
  className,
}: {
  icon: LucideIcon;
  title: string;
  text?: string;
  tone?: 'neutral' | 'danger' | 'warning' | 'accent';
  busy?: boolean;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex flex-col items-center px-6 text-center', className)}
    >
      <span
        className={cn(
          'mb-5 grid size-14 place-items-center rounded-2xl ring-1',
          tone === 'neutral' && 'bg-ink-700 text-fg-muted ring-line-strong',
          tone === 'accent' && 'bg-volt-500/12 text-volt-300 ring-volt-500/30',
          tone === 'danger' && 'bg-danger/10 text-danger ring-danger/25',
          tone === 'warning' && 'bg-warning/10 text-warning ring-warning/25',
        )}
      >
        <Icon className={cn('size-6', busy && 'animate-spin')} strokeWidth={1.75} />
      </span>
      <h2 className="text-lg font-bold tracking-tight text-fg">{title}</h2>
      {text ? <p className="mt-2 max-w-sm text-sm leading-relaxed text-fg-muted">{text}</p> : null}
      {actions ? <div className="mt-6 flex flex-wrap justify-center gap-3">{actions}</div> : null}
    </div>
  );
}

export function FullPageState(props: Parameters<typeof StatePanel>[0]) {
  return (
    <main className="grid min-h-dvh place-items-center bg-ink-950 py-16">
      <StatePanel {...props} />
    </main>
  );
}
